import type { ILoginSuccessView } from '@shared/services';
import { authApi } from '../../client';
import { type ILoginChallengeView, type IMfaChallengeView } from './login.request';

/**
 * Issuing an account switch from inside `auth-ui`. Panels never call `switch-account` themselves;
 * `auth-ui` owns the call. Unlike `authClient.switchAccount` (which adopts the returned session
 * immediately), this thin request returns the raw view so the caller decides when to adopt. It goes
 * through the authorized `authApi` (Bearer access token + refresh cookie) because a switch is only
 * valid for an already-signed-in session bootstrapped via `authClient.ensureAccessToken`.
 *
 * Deliberately NO `x-auth-supports-challenges` header: gates are resolved at login time only, and
 * without the opt-in the backend (current or legacy) never answers a switch with a challenge — a
 * target whose account would gate at login is switched into normally, with the gate enforced at
 * that account's next own login.
 */
export interface SwitchAccountRequestParams {
  userId: number;
  /** Location to activate in the target account, subject to the same rules as login. */
  locationId?: number;
}

// Expected: authenticated success or a thrown error. The challenge views stay in the union only so
// the caller can discriminate a legacy challenge body defensively — it must never be adopted.
export type SwitchAccountResponse = ILoginSuccessView | IMfaChallengeView | ILoginChallengeView;

export async function switchAccountRequest(params: SwitchAccountRequestParams): Promise<SwitchAccountResponse> {
  const { data } = await authApi.post('/api/v1/auth/switch-account', params);

  return data;
}
